import type {
  AnswersByKey,
  EmailStep,
  FlowVariant,
  FunnelOption,
  FunnelOptionFeedback,
  FunnelStep,
  LoadingStep,
  PaymentPlanOption,
  PaymentStep,
  QuestionStep,
  ResultStep,
  StepFeatureItem,
  WelcomeStep,
  WelcomeVariant,
} from "./types";
import { config } from "../config";

export const VIREO_SOCIAL_PROOF = {
  rating: "4.7",
  reviewCount: "1000+",
} as const;

export const VIREO_SOCIAL_PROOF_INLINE = `${VIREO_SOCIAL_PROOF.reviewCount} founding members`;

export const VIREO_RESULT_PROFILE_ITEMS = [
  "1 clear insight into what is interrupting your daily rhythm",
  "A ranked roadmap for what to practice first",
  "A grounded 14-day ritual matched to your goals",
] as const;

export const VIREO_EMAIL_VALUE_STACK = [
  "Your private Sehaj profile summary",
  "A ranked practice plan with highest-impact next steps first",
  "A clear 14-day Saakhi (story) and Simran (remembrance) rhythm",
] as const;

/**
 * Instant-render and outage fallback only. Provider prices returned by the
 * checkout Worker are authoritative for display, checkout, and analytics.
 */
export const SEHAJ_FALLBACK_PLANS: PaymentPlanOption[] = [
  {
    id: "weekly",
    label: "1 week",
    savePercent: 40,
    weeksInPeriod: 1,
    totalPrice: "$4.99",
  },
  {
    id: "monthly",
    label: "1 month",
    savePercent: 40,
    weeksInPeriod: 4.33,
    totalPrice: "$9.99",
  },
  {
    id: "quarterly",
    label: "3 months",
    savePercent: 50,
    weeksInPeriod: 13,
    totalPrice: "$24.99",
    isMostPopular: true,
  },
  {
    id: "annual",
    label: "1 year",
    savePercent: 70,
    weeksInPeriod: 52,
    totalPrice: "$47.00",
  },
];

export const VIREO_TRUST_BADGE_AVATARS = [
  { src: "/sehaj/testimonials/amrit-k.webp", alt: "Amrit" },
  { src: "/sehaj/testimonials/gurpreet-s.webp", alt: "Gurpreet" },
  { src: "/sehaj/testimonials/harleen-m.webp", alt: "Harleen" },
  { src: "/sehaj/testimonials/jess.webp", alt: "Jess" },
] as const;

export const VIREO_WELCOME_RATING_TEXT = `Joined by <b>${VIREO_SOCIAL_PROOF.reviewCount}</b> members`;
export const VIREO_WELCOME_TRUST_MEMBER_SUBTITLE = "building a daily practice";
export const SEHAJ_WELCOME_TRUST_PRIVACY_ITEMS = [
  { icon: "lock", label: "Private by default" },
  { icon: "shield", label: "Stories for your daily rhythm" },
  { icon: "lock", label: "Made for real life" },
] as const;
export const VIREO_WELCOME_TRUST_BADGE_CAPTION =
  "Private by default · No public sharing · Secure processing";
export const VIREO_FEATURED_IN_PUBLICATIONS = [
  "Community-led",
  "Calm by design",
  "Daily practice",
] as const;

type FlowValue<T> = T | { a: T; b: T };

const split = <T>(a: T, b: T): FlowValue<T> => ({ a, b });

const isSplitValue = <T>(value: FlowValue<T>): value is { a: T; b: T } => {
  return (
    typeof value === "object" && value !== null && "a" in value && "b" in value
  );
};

const resolveByFlow = <T>(value: FlowValue<T>, flow: FlowVariant): T => {
  return isSplitValue(value) ? value[flow] : value;
};

type RawWelcomeStep = Omit<
  WelcomeStep,
  | "title"
  | "description"
  | "ratingText"
  | "trustMemberSubtitle"
  | "trustBadgeCaption"
  | "trustPrivacyItems"
  | "trustBadgeAvatars"
  | "featuredIn"
  | "translation"
  | "embeddedQuestion"
  | "heroImage"
  | "confirmLabel"
  | "heroSlides"
  | "featurePills"
  | "finePrint"
> & {
  title: FlowValue<string>;
  description: FlowValue<string[]>;
  ratingText: FlowValue<string>;
  translation?: FlowValue<string>;
  trustMemberSubtitle?: FlowValue<string>;
  trustBadgeCaption?: FlowValue<string>;
  trustPrivacyItems?: FlowValue<WelcomeStep["trustPrivacyItems"]>;
  trustBadgeAvatars?: FlowValue<WelcomeStep["trustBadgeAvatars"]>;
  featuredIn?: FlowValue<string[]>;
  embeddedQuestion?: FlowValue<WelcomeStep["embeddedQuestion"]>;
  heroImage?: FlowValue<string>;
  confirmLabel: FlowValue<string>;
  heroSlides?: FlowValue<WelcomeStep["heroSlides"]>;
  featurePills?: FlowValue<WelcomeStep["featurePills"]>;
  finePrint?: FlowValue<string>;
};

type RawQuestionStep = Omit<
  QuestionStep,
  | "title"
  | "subtitle"
  | "translation"
  | "helperText"
  | "options"
  | "mediaLabel"
  | "mediaImage"
  | "footerNote"
> & {
  title: FlowValue<string>;
  subtitle?: FlowValue<string>;
  translation?: FlowValue<string>;
  helperText?: FlowValue<string>;
  options: FlowValue<FunnelOption[]>;
  mediaLabel?: FlowValue<string>;
  mediaImage?: FlowValue<string>;
  footerNote?: FlowValue<string>;
  optionFeedback?: Record<string, FunnelOptionFeedback>;
};

type RawAffirmationStep = Omit<
  Extract<FunnelStep, { kind: "affirmation" }>,
  | "title"
  | "description"
  | "translation"
  | "confirmLabel"
  | "mediaLabel"
  | "heroImage"
  | "features"
  | "footerNote"
> & {
  title: FlowValue<string>;
  description: FlowValue<string[]>;
  translation?: FlowValue<string>;
  confirmLabel: FlowValue<string>;
  heroImage?: FlowValue<string>;
  mediaLabel?: FlowValue<string>;
  features?: FlowValue<StepFeatureItem[]>;
  footerNote?: FlowValue<string>;
};

type RawLoadingStep = Omit<
  LoadingStep,
  | "title"
  | "subtitle"
  | "translation"
  | "confirmLabel"
  | "loadingFact"
  | "features"
  | "footerNote"
> & {
  title: FlowValue<string>;
  subtitle?: FlowValue<string>;
  translation?: FlowValue<string>;
  confirmLabel: FlowValue<string>;
  loadingFact: FlowValue<string>;
  heroImage?: FlowValue<string>;
  features?: FlowValue<StepFeatureItem[]>;
  footerNote?: FlowValue<string>;
};

type RawResultStep = Omit<
  ResultStep,
  | "title"
  | "subtitle"
  | "translation"
  | "personaName"
  | "description"
  | "questMap"
  | "confirmLabel"
  | "features"
  | "footerNote"
> & {
  title: FlowValue<string>;
  subtitle?: FlowValue<string>;
  translation?: FlowValue<string>;
  personaName: FlowValue<string>;
  description: FlowValue<string[]>;
  questMap: FlowValue<ResultStep["questMap"]>;
  confirmLabel: FlowValue<string>;
  features?: FlowValue<StepFeatureItem[]>;
  footerNote?: FlowValue<string>;
};

type RawEmailStep = Omit<
  EmailStep,
  "title" | "subtitle" | "description" | "confirmLabel" | "secondaryCta"
> & {
  title: FlowValue<string>;
  subtitle?: FlowValue<string>;
  description: FlowValue<string[]>;
  confirmLabel: FlowValue<string>;
  secondaryCta?: FlowValue<string>;
};

type RawPaymentStep = Omit<
  PaymentStep,
  | "title"
  | "subtitle"
  | "description"
  | "benefits"
  | "plans"
  | "proofBullets"
  | "testimonials"
  | "confirmLabel"
  | "whyCardCopy"
  | "promoBadge"
> & {
  title: FlowValue<string>;
  subtitle?: FlowValue<string>;
  description: FlowValue<string[]>;
  benefits: FlowValue<string[]>;
  plans: FlowValue<PaymentStep["plans"]>;
  proofBullets: FlowValue<string[]>;
  testimonials: FlowValue<PaymentStep["testimonials"]>;
  confirmLabel: FlowValue<string>;
  whyCardCopy: FlowValue<string>;
  promoBadge?: FlowValue<string>;
};

type RawStep =
  | RawWelcomeStep
  | RawQuestionStep
  | RawAffirmationStep
  | RawLoadingStep
  | RawResultStep
  | RawEmailStep
  | RawPaymentStep;

const yesNoOptions: FunnelOption[] = [
  { id: "yes", label: "Yes", emoji: "✅" },
  { id: "no", label: "No", emoji: "❌" },
];

const PRACTICE_INTENT_OPTIONS: FunnelOption[] = [
  {
    id: "just_me",
    label: "I want more grounding in my day",
  },
  {
    id: "partner",
    label: "I want more consistency in my practice",
  },
  {
    id: "family",
    label: "I want to feel closer to Sikhi",
  },
  {
    id: "just_starting",
    label: "I'm just starting",
  },
];

const PRACTICE_INTENT_WELCOME_OPTIONS: FunnelOption[] = [
  {
    id: "just_me",
    label: "Grounding in my day",
    description: "A calmer return point for real life",
  },
  {
    id: "partner",
    label: "Consistency in practice",
    description: "A rhythm I can actually keep",
  },
  {
    id: "family",
    label: "Connection to Sikhi",
    description: "A deeper sense of closeness and meaning",
  },
  {
    id: "just_starting",
    label: "Just starting",
    description: "Gentle guidance without overwhelm",
  },
] as const;

// ─── Persona system ──────────────────────────────────────────────────────────

export type PersonaKey =
  | "early_seeker"
  | "quiet_builder"
  | "curious_practitioner"
  | "grounded_planner"
  | "reflective_heart"
  | "deepening_practitioner";

type PersonaData = {
  heroImage: string;
  personaName: string;
  description: string[];
  questMap: ResultStep["questMap"];
};

type PersonaCopy = Omit<PersonaData, "questMap">;

const PERSONAS: Record<PersonaKey, PersonaCopy> = {
  early_seeker: {
    heroImage: "/sehaj/quiz/persona-path-ready.webp",
    personaName: "Early Seeker",
    description: [
      "Your intention is strong. The biggest win now is consistency. Sehaj helps you start with one small step so your daily practice feels natural, not heavy.",
    ],
  },
  quiet_builder: {
    heroImage: "/sehaj/quiz/persona-path-ready.webp",
    personaName: "Quiet Builder",
    description: [
      "You are looking for a clear place to begin and a rhythm you can keep. Sehaj starts with one simple, repeatable step so your connection to Sikhi can grow steadily.",
    ],
  },
  curious_practitioner: {
    heroImage: "/sehaj/quiz/persona-path-ready.webp",
    personaName: "Curious Practitioner",
    description: [
      "You are open to growth and new approaches. Sehaj helps you channel that curiosity into grounded Saakhi (story) and Simran moments that you can sustain.",
    ],
  },
  grounded_planner: {
    heroImage: "/sehaj/quiz/persona-path-ready.webp",
    personaName: "Grounded Planner",
    description: [
      "You value a clear structure and a rhythm that fits real life. Sehaj gives you gentle sequencing and one grounded next step without making practice feel heavy.",
    ],
  },
  reflective_heart: {
    heroImage: "/sehaj/quiz/persona-path-ready.webp",
    personaName: "Reflective Heart",
    description: [
      "You are drawn to reflection and deeper meaning. Sehaj adds a little structure around when and how you practice so that depth can remain calm and sustainable.",
    ],
  },
  deepening_practitioner: {
    heroImage: "/sehaj/quiz/persona-path-ready.webp",
    personaName: "Deepening Practitioner",
    description: [
      "You want depth as well as structure. Sehaj sequences fuller Saakhis and guided practice so each step builds with clarity instead of feeling scattered.",
    ],
  },
};

export const getPersonaHeroImages = (): string[] => {
  return Object.values(PERSONAS).map((persona) => persona.heroImage);
};

const PERSONA_ALIASES: Record<string, PersonaKey> = {
  early_seeker: "early_seeker",
  earlyseeker: "early_seeker",
  quiet_builder: "quiet_builder",
  quietbuilder: "quiet_builder",
  curious_practitioner: "curious_practitioner",
  curiouspractitioner: "curious_practitioner",
  grounded_planner: "grounded_planner",
  groundedplanner: "grounded_planner",
  reflective_heart: "reflective_heart",
  reflectiveheart: "reflective_heart",
  deepening_practitioner: "deepening_practitioner",
  deepeningpractitioner: "deepening_practitioner",
  // Legacy persona keys (pre–Sikhi naming)
  pantry_raider: "early_seeker",
  pantryraider: "early_seeker",
  steady_cook: "quiet_builder",
  steadycook: "quiet_builder",
  explorer: "curious_practitioner",
  planner: "grounded_planner",
  natural: "reflective_heart",
  kitchen_devotee: "deepening_practitioner",
  kitchendevotee: "deepening_practitioner",
};

const normalizePersonaAlias = (value: string): string => {
  return value.toLowerCase().replace(/[^a-z0-9]/g, "_");
};

const resolvePersonaOverride = (value: string | null): PersonaKey | null => {
  if (!value) {
    return null;
  }

  const normalized = normalizePersonaAlias(value);
  return PERSONA_ALIASES[normalized] ?? null;
};

const str = (answers: AnswersByKey, key: string): string =>
  typeof answers[key] === "string" ? answers[key] : "";

const arr = (answers: AnswersByKey, key: string): string[] =>
  Array.isArray(answers[key]) ? (answers[key] as string[]) : [];

const computeTraitMap = (answers: AnswersByKey): ResultStep["questMap"] => {
  const goals = arr(answers, "practice_priorities");
  const why = arr(answers, "your_why");

  const guidanceStyleScores: Record<string, number> = {
    short_anchor: 1,
    story_first: 2,
    deeper_guided: 4,
    // Legacy option ids
    quick_15: 1,
    one_pot: 2,
    adventurous: 4,
  };
  const saakhiFamiliarity: Record<string, number> = {
    yes_absolutely: 3,
    heard_not_sure: 1,
    no_idea: 0,
  };
  const saakhiAudienceIntent: Record<string, number> = {
    just_me: 1,
    family: 2,
    kids: 3,
  };

  const creativityRaw =
    (guidanceStyleScores[str(answers, "guidance_style")] ?? 2) +
    (goals.includes("deeper_saakhis") || goals.includes("new_cuisines") ? 2 : 0) +
    (saakhiFamiliarity[str(answers, "saakhi_familiarity")] ?? 1) +
    (str(answers, "rapid_fire_1") === "yes" ? 1 : 0) +
    (str(answers, "rapid_fire_2") === "yes" ? 1 : 0) +
    (str(answers, "new_ingredients") === "yes" ? 1 : 0);

  const weeklyScores: Record<string, number> = {
    rarely: 0,
    one_two: 1,
    three_four: 2,
    almost_daily: 3,
  };
  const confidenceScores: Record<string, number> = {
    beginner: 0,
    inconsistent_practice: 1,
    comfortable: 2,
    masterchef: 3,
    follow_recipe: 1,
  };
  const practiceIntentScores: Record<string, number> = {
    just_me: 0,
    partner: 1,
    family: 2,
    just_starting: 2,
    friends_entertaining: 2,
  };

  const commitmentRaw =
    (weeklyScores[str(answers, "simran_frequency")] ?? 1) +
    (confidenceScores[str(answers, "practice_steadiness")] ?? 1) +
    (practiceIntentScores[str(answers, "practice_intent")] ?? 1);

  const knowledgeRaw =
    (confidenceScores[str(answers, "practice_steadiness")] ?? 1) +
    (saakhiFamiliarity[str(answers, "saakhi_familiarity")] ?? 1) +
    (str(answers, "rapid_fire_2") === "yes" ? 1 : 0);

  const depthIntent: Record<string, number> = {
    simple: 1,
    full_picture: 2,
    real_depth: 3,
  };

  const intentionalityRaw =
    (depthIntent[str(answers, "guidance_depth")] ?? 2) +
    (saakhiAudienceIntent[str(answers, "saakhi_audience")] ?? 1) +
    Math.min(goals.length, 5) +
    (why.includes("calmer_mind") || why.includes("eat_healthier") ? 1 : 0) +
    (why.includes("family_presence") || why.includes("family_enjoys") ? 1 : 0) +
    (why.includes("share_with_kids") || why.includes("teach_kids") ? 1 : 0) +
    (why.includes("closer_to_sikhi") || why.includes("enjoy_kitchen") ? 1 : 0);

  const BASE = 2;
  const creativity = creativityRaw + BASE;
  const commitment = commitmentRaw + BASE;
  const knowledge = knowledgeRaw + BASE;
  const intentionality = intentionalityRaw + BASE;
  const total = creativity + commitment + knowledge + intentionality;

  return [
    {
      id: "creativity",
      label: "Openness",
      percent: Math.round((creativity / total) * 100),
      icon: "◉",
    },
    {
      id: "commitment",
      label: "Consistency",
      percent: Math.round((commitment / total) * 100),
      icon: "◉",
    },
    {
      id: "knowledge",
      label: "Understanding",
      percent: Math.round((knowledge / total) * 100),
      icon: "◉",
    },
    {
      id: "intentionality",
      label: "Intention",
      percent: Math.round((intentionality / total) * 100),
      icon: "◉",
    },
  ].sort((a, b) => b.percent - a.percent);
};

export const resolvePersonaKey = (answers: AnswersByKey): PersonaKey => {
  const depthMap: Record<string, "casual" | "moderate" | "deep"> = {
    simple: "casual",
    full_picture: "moderate",
    real_depth: "deep",
  };
  const depth = depthMap[str(answers, "guidance_depth")] ?? "moderate";

  const styleMap: Record<string, "free" | "structured"> = {
    short_anchor: "structured",
    story_first: "free",
    deeper_guided: "free",
    quick_15: "structured",
    one_pot: "free",
    adventurous: "free",
  };
  const style = styleMap[str(answers, "guidance_style")] ?? "structured";

  if (depth === "casual" && style === "free") return "early_seeker";
  if (depth === "casual" && style === "structured") return "quiet_builder";
  if (depth === "moderate" && style === "free") return "curious_practitioner";
  if (depth === "moderate" && style === "structured") return "grounded_planner";
  if (depth === "deep" && style === "free") return "reflective_heart";
  return "deepening_practitioner";
};

export const getPersonaData = (answers: AnswersByKey): PersonaData => ({
  ...PERSONAS[resolvePersonaKey(answers)],
  questMap: computeTraitMap(answers),
});

export const resolvePersonaOverrideFromUrl = (
  urlLike: URL | string = window.location.href,
): PersonaKey | null => {
  const url = typeof urlLike === "string" ? new URL(urlLike) : urlLike;
  const override =
    resolvePersonaOverride(url.searchParams.get("persona")) ??
    resolvePersonaOverride(url.searchParams.get("persona_override"));
  return override;
};

export const getPersonaDataWithOverride = (
  answers: AnswersByKey,
  overridePersona: PersonaKey | null,
): PersonaData => ({
  ...PERSONAS[overridePersona ?? resolvePersonaKey(answers)],
  questMap: computeTraitMap(answers),
});

// ─────────────────────────────────────────────────────────────────────────────

const RAW_STEPS: RawStep[] = [
  {
    id: "welcome",
    order: 1,
    kind: "welcome",
    heroImage: "/sehaj/Imagery/hand-reading-sikh-story.webp",
    title: split(
      "Close to Sikhi in 5 minutes",
      "Close to Sikhi in 5 minutes",
    ),
    translation: split(
      "ਸਾਖੀ · ਸਿਮਰਨ — one small step today",
      "ਸਾਖੀ · ਸਿਮਰਨ — one small step today",
    ),
    description: split(
      [
        "Start with a short Saakhi and a moment of Simran. No pressure — just one step today.",
      ],
      [
        "Start with a short Saakhi and a moment of Simran. No pressure — just one step today.",
      ],
    ),
    ratingText: split(VIREO_WELCOME_RATING_TEXT, VIREO_WELCOME_RATING_TEXT),
    trustMemberSubtitle: split(
      VIREO_WELCOME_TRUST_MEMBER_SUBTITLE,
      VIREO_WELCOME_TRUST_MEMBER_SUBTITLE,
    ),
    trustPrivacyItems: split(
      [...SEHAJ_WELCOME_TRUST_PRIVACY_ITEMS],
      [...SEHAJ_WELCOME_TRUST_PRIVACY_ITEMS],
    ),
    trustBadgeAvatars: split(
      [...VIREO_TRUST_BADGE_AVATARS],
      [...VIREO_TRUST_BADGE_AVATARS],
    ),
    featuredIn: split([], []),
    embeddedQuestion: split(undefined, {
      answerKey: "practice_intent",
      title: "Where would a calmer rhythm help most right now?",
      options: PRACTICE_INTENT_WELCOME_OPTIONS,
    }),
    confirmLabel: split("Start my journey", "Start my journey"),
    finePrint: split("No commitment · about 60 seconds", "No commitment · about 60 seconds"),
  },
  {
    id: "practice_intent",
    order: 2,
    kind: "question",
    selection: "single",
    answerKey: "practice_intent",
    mediaImage: "/sehaj/paywall/history-guru-nanak-sahib.webp",
    title: "What feels most true right now?",
    translation: "ਤੁਹਾਡੇ ਲਈ ਹੁਣ ਕੀ ਸਭ ਤੋਂ ਸੱਚਾ ਹੈ?",
    helperText: "Choose one. We'll take it from there.",
    options: PRACTICE_INTENT_OPTIONS,
    confirmLabel: "See my path →",
  },
  {
    id: "saakhi_familiarity",
    order: 3,
    kind: "question",
    selection: "single",
    answerKey: "saakhi_familiarity",
    mediaLabel: "Practice awareness",
    mediaImage: "/sehaj/quiz/bhai-lalo-tablet-landscape.webp",
    title: "Do you know the Saakhi of Bhai Lalo?",
    translation: "ਸਾਖੀ · a story that carries wisdom",
    options: [
      { id: "yes_absolutely", label: "Yes, I know it well", emoji: "✅" },
      { id: "heard_not_sure", label: "I've heard it, not clearly", emoji: "🤔" },
      { id: "no_idea", label: "Not yet", emoji: "❓" },
    ],
    confirmLabel: "Continue →",
  },
  {
    id: "saakhi_audience",
    order: 4,
    kind: "question",
    selection: "single",
    answerKey: "saakhi_audience",
    mediaImage: "/sehaj/quiz/audience-storytelling-child.webp",
    title: "Who are you exploring Sikh stories for?",
    translation: "ਆਪਣੇ ਲਈ · ਪਰਿਵਾਰ ਲਈ · ਬੱਚਿਆਂ ਲਈ",
    options: [
      { id: "just_me", label: "For myself", emoji: "🙌" },
      { id: "family", label: "For my family", emoji: "👍" },
      { id: "kids", label: "For my kids", emoji: "🌱" },
    ],
    confirmLabel: "Continue →",
  },
  {
    id: "practice_steadiness",
    order: 5,
    kind: "question",
    selection: "single",
    answerKey: "practice_steadiness",
    mediaImage: "/sehaj/quiz/practice-steadiness-quiet-space.webp",
    title: "How does your Sikhi daily practice feel right now?",
    translation: "ਤੁਹਾਡੀ ਰੋਜ਼ਾਨਾ ਸਿਖੀ ਅਭਿਆਸ ਹੁਣ ਕਿਵੇਂ ਮਹਿਸੂਸ ਹੁੰਦਾ ਹੈ?",
    options: [
      { id: "beginner", label: "I'm just beginning", emoji: "🌱" },
      { id: "inconsistent_practice", label: "I try, but it feels inconsistent", emoji: "📖" },
      { id: "comfortable", label: "I have a basic rhythm", emoji: "🧭" },
      { id: "masterchef", label: "I'm steady and ready to deepen", emoji: "🏆" },
    ],
    confirmLabel: "Continue →",
  },
  {
    id: "practice_priorities",
    order: 6,
    kind: "question",
    selection: "multi",
    answerKey: "practice_priorities",
    mediaImage: "/sehaj/quiz/practice-priorities-study-hands.webp",
    title: "What would help your practice feel steadier first?",
    subtitle: "Saakhi stories · Simran remembrance",
    translation: "ਸਾਖੀ · ਸਿਮਰਨ",
    footerNote: "Choose every area that feels true today.",
    minSelections: 1,
    options: [
      { id: "daily_consistency", label: "Daily consistency", emoji: "🧩" },
      { id: "calm_mind_speech", label: "Calm in mind and speech", emoji: "📐" },
      { id: "deeper_saakhis", label: "Understand Saakhis more deeply", emoji: "✨" },
      { id: "five_min_routine", label: "A 5-minute routine I can keep", emoji: "📷" },
    ],
    confirmLabel: "Continue →",
  },
  {
    id: "biggest_blocker",
    order: 7,
    kind: "question",
    selection: "single",
    answerKey: "biggest_blocker",
    mediaImage: "/sehaj/quiz/biggest-blocker-returning-path.webp",
    title: "What interrupts your practice most often?",
    translation: "ਕਿਹੜੀ ਗੱਲ ਰਾਹ ਰੋਕਦੀ ਹੈ?",
    options: [
      { id: "too_tired", label: "I feel mentally overloaded", emoji: "😮‍💨" },
      { id: "dont_know_what", label: "I don't know where to begin", emoji: "🤷" },
      {
        id: "ingredients_waste",
        label: "I start, then lose momentum",
        emoji: "🧪",
      },
      {
        id: "too_complicated",
        label: "It feels difficult to relate",
        emoji: "🧩",
      },
    ],
    optionFeedback: {
      too_tired: {
        text: "That is human, not failure. One small return still counts.",
        variant: "encouraging",
      },
      dont_know_what: {
        text: "Clarity helps. We will give you one simple first step.",
        variant: "positive",
      },
      ingredients_waste: {
        text: "Small wins create momentum. Your next step will feel clear and doable.",
        variant: "positive",
      },
      too_complicated: {
        text: "Depth does not need to feel distant. We will keep it clear and grounded.",
        variant: "encouraging",
      },
    },
    confirmLabel: "Continue →",
  },
  {
    id: "affirmation_1",
    order: 8,
    kind: "affirmation",
    progress: 32,
    title: "We'll meet you where you are",
    reassuranceMetric: {
      value: "87%",
      description: "of Sehaj users say their Saakhis feel relevant to the topics they care about.",
    },
    translation: "ਅਸੀਂ ਤੁਹਾਡੇ ਨਾਲ, ਤੁਹਾਡੀ ਰਫ਼ਤਾਰ ਨਾਲ।",
    description: [
      "Your first rhythm will be simple, supportive, and shaped around you.",
    ],
    heroImage: "/sehaj/Imagery/sikhs-walking-away-old-attire.webp",
    heroImageHasBorderRadius: true,
    heroImageBorderRadius: 20,
    heroImageMarginBottom: 10,
    confirmLabel: "Find my daily rhythm →",
  },
  {
    id: "simran_frequency",
    order: 9,
    kind: "question",
    selection: "single",
    answerKey: "simran_frequency",
    mediaImage: "/sehaj/quiz/simran-morning-stillness.webp",
    title: "How often do you make space for Simran or reflection?",
    translation: "ਸਿਮਰਨ · remembrance",
    options: [
      { id: "rarely", label: "Rarely", emoji: "🫥" },
      { id: "one_two", label: "1–2 days a week", emoji: "🗓️" },
      { id: "three_four", label: "3–4 days a week", emoji: "📈" },
      { id: "almost_daily", label: "Most days", emoji: "✨" },
    ],
    confirmLabel: "Continue →",
  },
  {
    id: "guidance_style",
    order: 10,
    kind: "question",
    selection: "single",
    answerKey: "guidance_style",
    mediaImage: "/sehaj/quiz/guidance-style-ritual-tools.webp",
    title: "What kind of guidance feels right for you?",
    translation: "ਤੁਹਾਡੀ ਰਫ਼ਤਾਰ. ਤੁਹਾਡਾ ਕਦਮ.",
    options: [
      { id: "short_anchor", label: "Short daily anchor", emoji: "⚡" },
      { id: "story_first", label: "Story-first guidance", emoji: "🧱" },
      { id: "deeper_guided", label: "Deeper guided practice", emoji: "🧪" },
    ],
    confirmLabel: "Continue →",
  },
  {
    id: "affirmation_2",
    order: 11,
    kind: "affirmation",
    icon: "🍳",
    progress: 50,
    title: "A daily Sikhi rhythm, shaped around you",
    translation: "ਅਸੀਂ ਤੁਹਾਡੇ ਨਾਲ ਹਾਂ, ਹਰ ਕਦਮ ਤੇ।",
    heroImage: "/sehaj/Imagery/sikh%20man%20looking%20away%20in%20distant.png",
    heroImageHasBorderRadius: true,
    heroImageBorderRadius: 20,
    heroImageMarginBottom: 10,
    description: [
      "From your answers, we're choosing stories, reflections, and first steps that feel clear and personal.",
    ],
    features: [
      { icon: "shield", title: "Private by default", description: "Your answers stay with you" },
      { icon: "sprout", title: "Grounded in Sikhi", description: "Saakhis and Simran, clearly explained" },
      { icon: "focus", title: "Made for real life", description: "One calm next step you can keep" },
    ],
    featureLayout: "inline",
    confirmLabel: "Continue →",
  },
  {
    id: "guidance_depth",
    order: 12,
    kind: "question",
    selection: "single",
    answerKey: "guidance_depth",
    mediaImage: "/sehaj/quiz/guidance-depth-shared-learning.webp",
    title: "How much guidance feels right for your first step?",
    translation: "ਇਕ ਕਦਮ. ਹਰ ਰੋਜ਼.",
    options: [
      {
        id: "simple",
        label: "Keep it simple",
        description: "One clear insight and a calm first step",
        emoji: "🌱",
      },
      {
        id: "full_picture",
        label: "Show me the full picture",
        description: "A fuller Saakhi path and steady roadmap",
        emoji: "📖",
        badge: "Most popular",
      },
      {
        id: "real_depth",
        label: "I want deeper guidance",
        description: "A fuller Saakhi and Simran rhythm",
        emoji: "🎓",
      },
    ],
    confirmLabel: "Continue →",
  },
  {
    id: "rapid_fire_1",
    order: 13,
    kind: "question",
    selection: "single",
    answerKey: "rapid_fire_1",
    mediaLabel: "Daily rhythm",
    mediaImage: "/sehaj/Imagery/warm-light-sikh-reading-room.png",
    title:
      "Would you follow a 14-day Saakhi rhythm if each day began with one small step?",
    options: yesNoOptions,
    confirmLabel: "Continue →",
  },
  {
    id: "rapid_fire_2",
    order: 14,
    kind: "question",
    selection: "single",
    answerKey: "rapid_fire_2",
    mediaLabel: "Life context",
    mediaImage: "/sehaj/quiz/commute-phone-story.webp",
    title: "Would a short explanation help each Saakhi land in daily life?",
    options: yesNoOptions,
    confirmLabel: "Continue →",
  },
  {
    id: "your_why",
    order: 15,
    kind: "question",
    selection: "multi",
    answerKey: "your_why",
    mediaImage: "/sehaj/paywall/bento-story-ipad.webp",
    title: "Why does this matter right now?",
    helperText: "Choose all that feel true.",
    minSelections: 1,
    options: [
      { id: "calmer_mind", label: "I want a calmer mind", emoji: "🧭" },
      {
        id: "family_presence",
        label: "I want to show up better for family",
        emoji: "👥",
      },
      { id: "share_with_kids", label: "I want something real to share with my kids", emoji: "🌱" },
      {
        id: "closer_to_sikhi",
        label: "I want to feel closer to Sikhi",
        emoji: "✨",
      },
    ],
    confirmLabel: "Continue →",
  },
  {
    id: "generation_loading",
    order: 16,
    kind: "loading",
    progress: 85,
    heroImage: "/sehaj/brand/sehaj-icon.png",
    heroImageHasBorderRadius: true,
    title: "Preparing your Sehaj profile…",
    translation: "ਸਾਖੀ stories · ਸਿਮਰਨ remembrance",
    subtitle: "We're shaping one grounded next step you can keep returning to.",
    loadingFact:
      "A clear Saakhi lands more deeply when it meets the moment you are actually living.",
    modalQuestion: {
      answerKey: "new_ingredients",
      title:
        "Should your first Saakhi (story) be short or long - and more guided, or gentle?",
      options: [
        { id: "yes", label: "Short & gentle" },
        { id: "not_really", label: "Longer & guided" },
      ],
    },
    confirmLabel: "See my profile →",
  },
  {
    id: "persona_result",
    order: 17,
    kind: "result",
    icon: "🎯",
    title: "Your journey, one step at a time",
    subtitle: "How your next steps are being shaped",
    translation: "ਇਕ ਪਲ. ਇਕ ਕਦਮ. ਹਰ ਰੋਜ਼।",
    heroImageMarginBottom: 10,
    personaName: "The Steady Seeker",
    description: [
      "You are closer than you think. A few grounded shifts can create meaningful calm in daily life.",
      "Sehaj will prioritize Saakhi, Simran, and reflection so progress feels steady and real.",
    ],
    features: [
      { icon: "spark", title: "Meaningful Saakhis", description: "Stories that inspire and guide" },
      { icon: "headphones", title: "Centered Simran", description: "Practices to steady heart and mind" },
      { icon: "heart", title: "Reflection that stays with you", description: "Daily space for awareness and growth" },
    ],
    featureLayout: "stacked",
    questMap: [
      { id: "creativity", label: "Openness", percent: 30, icon: "◉" },
      { id: "commitment", label: "Consistency", percent: 28, icon: "◉" },
      { id: "knowledge", label: "Understanding", percent: 22, icon: "◉" },
      {
        id: "intentionality",
        label: "Intention",
        percent: 20,
        icon: "◉",
      },
    ],
    confirmLabel: "I’m ready →",
  },
  {
    id: "email_capture",
    order: 18,
    kind: "email",
    answerKey: "email",
    title: "Where should we send your first Saakhi?",
    description: ["Save your profile to continue."],
    emailPlaceholder: "your@email.com",
    confirmLabel: "Save my profile →",
    trustNote: "We’ll send Bhai Lalo Ji’s Saakhi and your private profile now.",
    quotes: [
      {
        text: "I grew up hearing the stories, but Sehaj helped me understand what one Saakhi meant for my day.",
        author: "Amrit K., 32",
        weeksUsingApp: 6,
        image: "/sehaj/testimonials/amrit-k.webp",
      },
      {
        text: "Instead of random motivation, I got one Sikh story, one reflection, and one clear next step.",
        author: "Gurpreet S., 37",
        weeksUsingApp: 11,
        image: "/sehaj/testimonials/gurpreet-s.webp",
      },
      {
        text: "The Saakhi and reflection gave me something simple and beautiful to share with my kids.",
        author: "Harleen M., 36",
        weeksUsingApp: 8,
        image: "/sehaj/testimonials/harleen-m.webp",
      },
      {
        text: "It felt respectful, familiar, and easy to return to even during a busy week.",
        author: "Navdeep B., 38",
        weeksUsingApp: 14,
      },
    ],
  },
  {
    id: "founding_member",
    order: 19,
    kind: "payment",
    answerKey: "selected_plan",
    title: "A little closer to Sikhi, every day",
    subtitle: "Your daily Sehaj rhythm",
    translation: "ਇੱਕ ਨਿੱਜੀ ਰਿਥਮ · ਕੋਈ ਦਬਾਅ ਨਹੀਂ · ਤੁਹਾਡੀ ਰਫ਼ਤਾਰ ਨਾਲ",
    description: [
      "Private, grounded guidance for a daily Sikhi ritual that sticks. No noise. No pressure.",
      "Choose the plan that fits your pace. You'll finish checkout securely.",
    ],
    plans: SEHAJ_FALLBACK_PLANS,
    benefits: [
      "Founding pricing locked in",
      "Early access to new Sehaj features",
      "Your private Sehaj profile and progress history saved",
    ],
    proofBullets: [
      "Personalized daily rhythm with ranked next steps",
      "Guided Saakhi (story) and Simran (remembrance) moments",
      "Progress you can revisit and track over time",
    ],
    testimonials: [
      {
        text: "I grew up hearing the stories, but Sehaj helped me understand what one Saakhi meant for my day.",
        author: "Amrit K., 32",
        weeksUsingApp: 6,
        image: "/sehaj/testimonials/amrit-k.webp",
      },
      {
        text: "Instead of random motivation, I got one Sikh story, one reflection, and one clear next step.",
        author: "Gurpreet S., 37",
        weeksUsingApp: 11,
        image: "/sehaj/testimonials/gurpreet-s.webp",
      },
      {
        text: "The Saakhi reflections helped me pause before reacting. It changed the tone of my evenings.",
        author: "Jaspreet K., 38",
        weeksUsingApp: 18,
        image: "/sehaj/testimonials/harleen-m.webp",
      },
      {
        text: "The guidance was calm and practical. It felt private, respectful, and true to Sikhi.",
        author: "Harjit B., 45",
        weeksUsingApp: 35,
        image: "/sehaj/testimonials/gurpreet-s.webp",
      },
      {
        text: "It gave me a simple way to bring one story and one reflection into family life.",
        author: "Simran D., 29",
        weeksUsingApp: 31,
        image: "/sehaj/testimonials/amrit-k.webp",
      },
    ],
    noChargeNote: "30-day money-back guarantee",
    securityNotes: ["Secure checkout", "Cancel anytime"],
    appScreenshots: [
      "/sehaj/app-screenshots/01-home.webp",
      "/sehaj/app-screenshots/02-saakhi.webp",
      "/sehaj/app-screenshots/03-listen.webp",
      "/sehaj/app-screenshots/04-reflection.webp",
      "/sehaj/app-screenshots/05-journey.webp",
    ],
    founderMessage: {
      fallbackText:
        "I grew up with Saakhis, then busy life got in the way. I’m building Sehaj to bring those stories and Sikh history into small daily moments - so we, and our children, can carry Sikhi’s wisdom into modern life.",
      founderName: "Jot",
      founderTitle: "Founder, Sehaj",
    },
    confirmLabel: "Continue to secure checkout →",
    whyCardCopy:
      "You don't need to change everything at once. You need one grounded next step, practiced with intention. Sehaj helps you return to that rhythm.",
  },
  {
    id: "checkout_complete",
    order: 20,
    kind: "affirmation",
    icon: "✅",
    title: "You're in. Your Sehaj access is secured",
    translation: "ਸਾਖੀ · ਸਿਮਰਨ — ਤੁਹਾਡਾ ਕਦਮ ਸੁਰੱਖਿਅਤ ਹੈ",
    description: [
      "Thank you for joining Sehaj. Because of unexpectedly high demand, we're onboarding in small waves so each member can start with care.",
    ],
    confirmLabel: "Open my email",
  },
];

const resolveStepByFlow = (step: RawStep, flow: FlowVariant): FunnelStep => {
  switch (step.kind) {
    case "welcome":
      return {
        ...step,
        title: resolveByFlow(step.title, flow),
        description: resolveByFlow(step.description, flow),
        ratingText: resolveByFlow(step.ratingText, flow),
        heroImage: step.heroImage
          ? resolveByFlow(step.heroImage, flow) || undefined
          : undefined,
        translation: step.translation
          ? resolveByFlow(step.translation, flow)
          : undefined,
        trustMemberSubtitle: step.trustMemberSubtitle
          ? resolveByFlow(step.trustMemberSubtitle, flow)
          : undefined,
        trustBadgeCaption: step.trustBadgeCaption
          ? resolveByFlow(step.trustBadgeCaption, flow)
          : undefined,
        trustPrivacyItems: step.trustPrivacyItems
          ? resolveByFlow(step.trustPrivacyItems, flow)
          : undefined,
        trustBadgeAvatars: step.trustBadgeAvatars
          ? resolveByFlow(step.trustBadgeAvatars, flow) || undefined
          : undefined,
        embeddedQuestion: step.embeddedQuestion
          ? resolveByFlow(step.embeddedQuestion, flow) || undefined
          : undefined,
        confirmLabel: resolveByFlow(step.confirmLabel, flow),
        heroSlides: step.heroSlides
          ? resolveByFlow(step.heroSlides, flow) || undefined
          : undefined,
        featurePills: step.featurePills
          ? resolveByFlow(step.featurePills, flow) || undefined
          : undefined,
        finePrint: step.finePrint
          ? resolveByFlow(step.finePrint, flow)
          : undefined,
        featuredIn: step.featuredIn
          ? resolveByFlow(step.featuredIn, flow) || undefined
          : undefined,
      };
    case "question":
      return {
        ...step,
        title: resolveByFlow(step.title, flow),
        subtitle: step.subtitle
          ? resolveByFlow(step.subtitle, flow)
          : undefined,
        translation: step.translation
          ? resolveByFlow(step.translation, flow)
          : undefined,
        helperText: step.helperText
          ? resolveByFlow(step.helperText, flow)
          : undefined,
        mediaLabel: step.mediaLabel
          ? resolveByFlow(step.mediaLabel, flow)
          : undefined,
        mediaImage: step.mediaImage
          ? resolveByFlow(step.mediaImage, flow) || undefined
          : undefined,
        footerNote: step.footerNote
          ? resolveByFlow(step.footerNote, flow)
          : undefined,
        options: resolveByFlow(step.options, flow),
        optionFeedback: step.optionFeedback,
      };
    case "affirmation":
      return {
        ...step,
        title: resolveByFlow(step.title, flow),
        description: resolveByFlow(step.description, flow),
        translation: step.translation
          ? resolveByFlow(step.translation, flow)
          : undefined,
        heroImage: step.heroImage
          ? resolveByFlow(step.heroImage, flow) || undefined
          : undefined,
        mediaLabel: step.mediaLabel
          ? resolveByFlow(step.mediaLabel, flow)
          : undefined,
        features: step.features
          ? resolveByFlow(step.features, flow) || undefined
          : undefined,
        footerNote: step.footerNote
          ? resolveByFlow(step.footerNote, flow)
          : undefined,
        confirmLabel: resolveByFlow(step.confirmLabel, flow),
      };
    case "loading":
      return {
        ...step,
        title: resolveByFlow(step.title, flow),
        subtitle: step.subtitle
          ? resolveByFlow(step.subtitle, flow)
          : undefined,
        translation: step.translation
          ? resolveByFlow(step.translation, flow)
          : undefined,
        confirmLabel: resolveByFlow(step.confirmLabel, flow),
        loadingFact: resolveByFlow(step.loadingFact, flow),
        heroImage: step.heroImage
          ? resolveByFlow(step.heroImage, flow) || undefined
          : undefined,
        features: step.features
          ? resolveByFlow(step.features, flow) || undefined
          : undefined,
        footerNote: step.footerNote
          ? resolveByFlow(step.footerNote, flow)
          : undefined,
      };
    case "result":
      return {
        ...step,
        title: resolveByFlow(step.title, flow),
        subtitle: step.subtitle
          ? resolveByFlow(step.subtitle, flow)
          : undefined,
        translation: step.translation
          ? resolveByFlow(step.translation, flow)
          : undefined,
        personaName: resolveByFlow(step.personaName, flow),
        description: resolveByFlow(step.description, flow),
        questMap: resolveByFlow(step.questMap, flow),
        features: step.features
          ? resolveByFlow(step.features, flow) || undefined
          : undefined,
        footerNote: step.footerNote
          ? resolveByFlow(step.footerNote, flow)
          : undefined,
        confirmLabel: resolveByFlow(step.confirmLabel, flow),
      };
    case "email": {
      const { secondaryCta: rawSecondaryCta, ...emailRest } = step;
      return {
        ...emailRest,
        title: resolveByFlow(step.title, flow),
        subtitle: step.subtitle
          ? resolveByFlow(step.subtitle, flow)
          : undefined,
        description: resolveByFlow(step.description, flow),
        confirmLabel: resolveByFlow(step.confirmLabel, flow),
        ...(rawSecondaryCta != null
          ? { secondaryCta: resolveByFlow(rawSecondaryCta, flow) }
          : {}),
      };
    }
    case "payment":
      return {
        ...step,
        title: resolveByFlow(step.title, flow),
        subtitle: step.subtitle
          ? resolveByFlow(step.subtitle, flow)
          : undefined,
        description: resolveByFlow(step.description, flow),
        benefits: resolveByFlow(step.benefits, flow),
        plans: resolveByFlow(step.plans, flow),
        proofBullets: resolveByFlow(step.proofBullets, flow),
        testimonials: resolveByFlow(step.testimonials, flow),
        confirmLabel: resolveByFlow(step.confirmLabel, flow),
        whyCardCopy: resolveByFlow(step.whyCardCopy, flow),
        promoBadge: step.promoBadge
          ? resolveByFlow(step.promoBadge, flow)
          : undefined,
      };
  }
};

const getEnabledRawSteps = (flow?: FlowVariant): RawStep[] => {
  return RAW_STEPS.filter((step) => {
    if (flow === "b" && step.id === "practice_intent") {
      return false;
    }

    if (step.id === "email_capture") {
      return config.enableEmailCapture;
    }

    return true;
  });
};

export const getFlowSteps = (flow: FlowVariant, welcomeVariant: WelcomeVariant = "default"): FunnelStep[] => {
  return getEnabledRawSteps(flow).map((step) => {
    const resolved = resolveStepByFlow(step, flow);
    return flow === "a" && welcomeVariant === "daily_routine" && resolved.kind === "welcome"
      ? { ...resolved, title: "Begin your daily Sikhi routine" }
      : resolved;
  });
};

export const TOTAL_STEPS = getEnabledRawSteps("a").length;

export const FLOW_META: Record<FlowVariant, { name: string; slug: string }> = {
  a: { name: "Quick check-in", slug: "quick-meals" },
  b: { name: "Guided check-in", slug: "full-kitchen" },
};

export type FunnelSection = {
  label: string;
  /** inclusive 0-based start index */
  start: number;
  /** inclusive 0-based end index */
  end: number;
};

const BASE_FUNNEL_SECTIONS: FunnelSection[] = [
  { label: "Your path", start: 0, end: 6 },
  { label: "Your rhythm", start: 7, end: 14 },
  { label: "Your Sehaj", start: 15, end: 19 },
];

export const getFunnelSections = (steps: FunnelStep[]): FunnelSection[] => {
  const maxIndex = steps.length - 1;
  return BASE_FUNNEL_SECTIONS.map((section, index) => ({
    label: section.label,
    start: Math.min(section.start, maxIndex),
    end:
      index === BASE_FUNNEL_SECTIONS.length - 1
        ? maxIndex
        : Math.min(section.end, maxIndex),
  })).filter((section) => section.start <= section.end);
};

export const computeSectionProgress = (
  currentIndex: number,
  sections: FunnelSection[],
): number[] => {
  return sections.map(({ start, end }) => {
    const size = end - start + 1;
    const completed = Math.min(Math.max(currentIndex - start, 0), size);
    return completed / size;
  });
};
